import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { Observable, forkJoin, of, take } from 'rxjs';
import { map } from 'rxjs/operators';

import { WorkspaceService } from '../../../shared/services/workspace.service';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { AuthService } from '../../../shared/services/auth.service';
import { TimelineService } from '../../../shared/services/timeline.service';
import { ThemeService } from '../../../shared/services/theme.service';
import { CryptoService } from '../../../shared/services/crypto.service';
import { MtmBreadcrumbService } from '../../../shared/services/mtm-breadcrumb.service';
import { GoogleAnalyticsService } from '../../../shared/services/google-analytics.service';

import {
  CAMPAIGN_WORKSPACE_ROUTES,
  CAMPAIGN_WORKSPACE_COMPONENT_NAMES,
} from '../../../shared/models/campaign-workspace-translations';
import { WorkspaceDTO } from '../../../shared/interfaces';
import { MtmBreadcrumbLink } from '../../../shared/services/mtm-breadcrumb.service';

@Injectable({
  providedIn: 'root',
})
export class CampaignDriveWorkSpaceService {
  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly workspaceService: WorkspaceService,
    private readonly analyticService: AnalyticsService,
    private readonly authService: AuthService,
    private readonly timelineService: TimelineService,
    private readonly themeService: ThemeService,
    private readonly cryptoService: CryptoService,
    private readonly mtmBreadcrumbService: MtmBreadcrumbService,
    private readonly googleAnalyticsService: GoogleAnalyticsService
  ) {}

  isInsideWorkspace(): Observable<boolean> {
    const currentUrl = this.router.url;
    const pathStartsWith = currentUrl.startsWith(CAMPAIGN_WORKSPACE_ROUTES.CAMPAIGN_DRIVE);

    return this.route.queryParams.pipe(map((params) => pathStartsWith && 'wid' in params));
  }

  getWorkspaceData(companyId: string, workspaceId: string): Observable<WorkspaceDTO | null> {
    const activeWorkspace = this.workspaceService.getActiveWorkspace();
    if (activeWorkspace && activeWorkspace.id === workspaceId) {
      this.timelineService.workspaceName = activeWorkspace.name;
      return of(activeWorkspace);
    }

    return this.workspaceService.getWorkspaceById(companyId, workspaceId, false, { redirectOnForbidden: true }).pipe(
      take(1),
      map((workspace: WorkspaceDTO | null) => {
        if (!workspace) return null;
        this.workspaceService.setActiveWorkspace(workspace);
        this.timelineService.workspaceName = workspace.name;
        return workspace;
      })
    );
  }

  getWorkspaces(
    workspaceId?: string,
    isStandAloneSearchEnabled = false
  ): Observable<{ workspaces: any[]; selectedWorkspace?: any; userBrands: any[] }> {
    return forkJoin([this.analyticService.getBrands(), this.analyticService.getActiveBrandWorkspaces()]).pipe(
      map(([brands, workspaces]: [any[], WorkspaceDTO[]]) => {
        const selectedWorkspace = workspaceId ? workspaces.find((w) => w.id === workspaceId) : undefined;

        this.themeService.updateContent({
          standaloneSearch: isStandAloneSearchEnabled || brands.length > 1,
        });

        if (selectedWorkspace) {
          this.changeWorkspace(selectedWorkspace);
        } else {
          const brand = this.authService.getMyBrand();
          this.timelineService.componentName.next({
            componentName: CAMPAIGN_WORKSPACE_COMPONENT_NAMES.CAMPAIGN_DRIVE_COMPONENT,
            bannerUrl: brand?.banner || null,
          });
        }

        return { workspaces, userBrands: brands };
      })
    );
  }

  changeWorkspace(workspace: any) {
    if (workspace === 'all_workspaces') {
      this.timelineService.workspaceId = '';
      const brand = this.authService.getMyBrand();
      this.timelineService.componentName.next({
        componentName: CAMPAIGN_WORKSPACE_COMPONENT_NAMES.CAMPAIGN_DRIVE_COMPONENT,
        bannerUrl: brand?.banner || null,
      });
      const navigationExtras: NavigationExtras = { queryParams: {} };
      this.router
        .navigate([CAMPAIGN_WORKSPACE_ROUTES.CAMPAIGN_DRIVE], navigationExtras)
        .then(() => {})
        .catch((err) => console.error('Navigation error:', err));
      return;
    }

    const workspaceId = workspace.id;
    this.timelineService.workspaceId = workspaceId;
    this.timelineService.workspaceName = workspace.name;
    this.timelineService.componentName.next({
      componentName: CAMPAIGN_WORKSPACE_COMPONENT_NAMES.CAMPAIGN_DRIVE_COMPONENT,
      bannerUrl: workspace.coverUrl,
    });

    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { wid: this.cryptoService.encryptString(workspaceId) },
        queryParamsHandling: 'merge',
      })
      .then(() => {})
      .catch((err) => console.error('Navigation error:', err));

    const brand = this.authService.getUserBrand();
    let pageLinks: MtmBreadcrumbLink[];

    if (workspaceId) {
      pageLinks = [
        { label: brand.name },
        { label: CAMPAIGN_WORKSPACE_COMPONENT_NAMES.WORKSPACE, url: [CAMPAIGN_WORKSPACE_ROUTES.WORKSPACES] },
        {
          label: workspace.name,
          url: [CAMPAIGN_WORKSPACE_ROUTES.WORKSPACES, CAMPAIGN_WORKSPACE_COMPONENT_NAMES.CAMPAIGN_DRIVE],
          queryParams: { wid: this.cryptoService.encryptString(workspaceId) },
        },
      ];
      this.googleAnalyticsService.sendUserAnalyticsConfig('', workspace.name, '');
    } else {
      pageLinks = [
        { label: brand.name, url: ['/'] },
        { label: CAMPAIGN_WORKSPACE_COMPONENT_NAMES.WORKSPACE, url: [CAMPAIGN_WORKSPACE_ROUTES.WORKSPACES] },
      ];
    }

    this.mtmBreadcrumbService.setLinks(pageLinks);
  }
}
